import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import AuroraHeatPumpModeControl from './AuroraHeatPumpModeControl'
import { AuroraAssetGroup } from '../types'
import { AuroraHeatPumpMode } from '../enums'
import { useAuroraStack } from '../hooks/aurora'
import { setAuroraHeatPumpMode } from '../services/aurora'
import { createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupHeatPumpModeControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (mode: AuroraHeatPumpMode) => void
  afterSubmit?: (mode: AuroraHeatPumpMode) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupHeatPumpModeControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupHeatPumpModeControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    awhpAuroras,
    heatPumpMode: initialHeatPumpMode,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [heatPumpMode, setHeatPumpMode] = useState<AuroraHeatPumpMode>(initialHeatPumpMode)

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  async function handleSubmit(value: AuroraHeatPumpMode) {
    try {
      setSubmitting(true)

      beforeSubmit?.(value)

      await Promise.all(awhpAuroras.map((aurora) => setAuroraHeatPumpMode(aurora.id, value)))

      dispatch(createSuccessNotification(t('setHeatPumpModeSuccess')))
      setHeatPumpMode(value)

      afterSubmit?.(value)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setHeatPumpModeError'))
    } finally {
      setSubmitting(false)
    }
  }

  if (awhpAuroras.length === 0) {
    return null
  }

  return (
    <AuroraHeatPumpModeControl
      value={heatPumpMode}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
